'use client';

import { c as _c } from "react/compiler-runtime";
import type { Role } from '@svenskaspelab/nova-core/nextjs/auth';
import { getFormattedCurrency } from '@svenskaspelab/poolbetting/core/lib/client';
import { useCouponId, useCouponIndices, useCouponsPrice, useCouponStatus, useCouponWallets, useHasPopulatedCoupon } from '@svenskaspelab/poolbetting/core/nextjs/coupon';
import { useEngine } from '@svenskaspelab/poolbetting/core/nextjs/engine';
import { useBalance } from '@svenskaspelab/poolbetting/core/nextjs/lib/client';
import type { DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import type { Definition } from '@svenskaspelab/poolbetting/schema/frameworks';
import type { RetailerBalanceResult } from '@svenskaspelab/poolbetting/schema/marketplace';
import { usePathname, useSearchParams } from 'next/navigation';
import { type ReactNode } from 'react';
import { type InjectedWalletProps, withContextWallet } from '@/components/index.browser';
import BetslipSrow from './BetslipSrow';
type BetslipSrowsClientContainerProps = {
  betslipContent: ReactNode;
  drawNumber: DrawNumber;
  retailerData?: RetailerBalanceResult;
  product: Definition['drawProducts'][number];
  role: Role;
};
type BetslipSrowsClientContainerPropsWithInjectedWallet = BetslipSrowsClientContainerProps & InjectedWalletProps;

/**
 * Component för standard betslip.
 *
 * @param root0 - The props object.
 * @param root0.betslipContent - The overview component to be displayed in the drawer
 * @param root0.retailerData - The retailer balance data
 * @param root0.drawNumber - The draw number to fetch marketplace balance for
 * @param root0.product - The product to fetch marketplace balance for
 * @param root0.contextWallets - The wallet data from context
 * @param root0.role - The role of the user (e.g., 'Retailer', 'Player')
 * @returns standard betslip component
 */
function BetslipSrowsClientContainer(t0) {
  const $ = _c(33);
  const {
    betslipContent,
    drawNumber,
    retailerData,
    product,
    contextWallets,
    role
  } = t0;
  const [couponId, couponIndex] = useCouponId();
  const pathname = usePathname();
  const searchParams = useSearchParams().toString();
  const returnUrl = searchParams ? `${pathname}?${searchParams}` : pathname;
  const definition = useEngine();
  let t1;
  if ($[0] !== couponId) {
    t1 = {
      couponId
    };
    $[0] = couponId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalCouponPrice = useCouponsPrice(t1);
  let t2;
  if ($[2] !== couponId) {
    t2 = {
      couponId
    };
    $[2] = couponId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const nrOfRows = useCouponIndices(t2).length;
  let t3;
  if ($[4] !== definition) {
    t3 = definition.hasAdditions();
    $[4] = definition;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasAdditions = t3;
  let t4;
  if ($[6] !== couponId) {
    t4 = {
      couponId
    };
    $[6] = couponId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const isPopulatedCoupon = useHasPopulatedCoupon(t4);
  let t5;
  if ($[8] !== couponId || $[9] !== couponIndex) {
    t5 = {
      couponId,
      couponIndex
    };
    $[8] = couponId;
    $[9] = couponIndex;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const couponStatus = useCouponStatus(t5);
  let t6;
  if ($[11] !== couponId) {
    t6 = {
      couponId
    };
    $[11] = couponId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const activeWallets = useCouponWallets(t6);
  let t7;
  if ($[13] !== activeWallets || $[14] !== contextWallets || $[15] !== couponIndex || $[16] !== drawNumber || $[17] !== product || $[18] !== retailerData || $[19] !== role) {
    t7 = {
      activeWallets,
      couponIndex,
      drawNumber,
      retailerData,
      product,
      contextWallets,
      role
    };
    $[13] = activeWallets;
    $[14] = contextWallets;
    $[15] = couponIndex;
    $[16] = drawNumber;
    $[17] = product;
    $[18] = retailerData;
    $[19] = role;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const {
    balance,
    shouldRedirectUser
  } = useBalance(t7);
  let t8;
  if ($[21] !== totalCouponPrice) {
    t8 = getFormattedCurrency(totalCouponPrice, {
      unit: "KR"
    });
    $[21] = totalCouponPrice;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== balance || $[24] !== betslipContent || $[25] !== couponStatus || $[26] !== hasAdditions || $[27] !== isPopulatedCoupon || $[28] !== nrOfRows || $[29] !== returnUrl || $[30] !== shouldRedirectUser || $[31] !== t8) {
    const betslipProps = {
      totalCouponPrice: t8,
      isPopulatedCoupon,
      hasAdditions,
      betslipContent,
      balance,
      shouldRedirectUser,
      nrOfRows,
      couponStatus,
      returnUrl
    };
    t9 = <BetslipSrow betslipProps={betslipProps} />;
    $[23] = balance;
    $[24] = betslipContent;
    $[25] = couponStatus;
    $[26] = hasAdditions;
    $[27] = isPopulatedCoupon;
    $[28] = nrOfRows;
    $[29] = returnUrl;
    $[30] = shouldRedirectUser;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
}
export default withContextWallet<BetslipSrowsClientContainerProps>(BetslipSrowsClientContainer);